/**
 * Гейт «Сделать видео» на карточке результата (видео-ролик из готового кадра, §3/§10 спеки
 * `2026-10-13-frame-to-video-design.md`): тот же гейт, что сервер держит на `POST
 * /api/results/variants/{id}/video` и отдаёт заранее через `GET .../video-gate`
 * (`videoscenario._frame_source`/`frame_gate`) — клип, видео-вариант и незавершённая генерация не
 * годятся опорным кадром ролика.
 *
 * Без импортов из `@/…`: модуль проверяется `node --test` (tests/frameVideo.test.ts) как есть.
 */

export type VideoGate = { ok: boolean; reason?: string };

/**
 * `kind` — вид генерации (`generation.kind`); `status` — её статус; `hasVideo` — у ЭТОГО варианта
 * есть файл-видео (`variant_video_ms` задан — карточка знает это как `card.video !== null`).
 */
export function canMakeVideo(kind: string, status: string, hasVideo: boolean): VideoGate {
  if (kind === "clip" || hasVideo) return { ok: false, reason: "это видео, а не кадр — нечего брать опорным изображением" };
  if (status !== "done") return { ok: false, reason: "генерация ещё не завершена" };
  return { ok: true };
}

/** Строка ленты «Результатов» в объёме, нужном гейту (`lib/results.ts`, `ResultGen`). */
export type FrameRow = {
  kind: string;
  status: string;
  variant_id: number | null;
  asset_path: string | null;
  /** Длительность файла-видео; у картинки — `null`/нет поля. */
  video_ms?: number | null;
  /** Метка этапа варианта: `picked` — «В улучшение»/«В публикацию» (подпись зависит от вида). */
  pick?: string;
};

/**
 * Отбор ленты «Результатов» для окна выбора опорного кадра на `/projects` (задача Оли №304): годится
 * то же, что и на карточке результата — правило одно, `canMakeVideo`, второго не заводим.
 *
 * Сверх гейта нужен сам файл варианта: строка в очереди или упавшая его не имеет, а окну нечего было
 * бы показать плиткой и нечего отдать создателю проекта. Порядок ленты сохраняем — свежие сверху.
 */
export function videoFrames<T extends FrameRow>(rows: readonly T[]): (T & { variant_id: number; asset_path: string })[] {
  return rows.filter(
    (r): r is T & { variant_id: number; asset_path: string } =>
      r.variant_id !== null &&
      Boolean(r.asset_path) &&
      canMakeVideo(r.kind, r.status, (r.video_ms ?? null) !== null).ok,
  );
}

/**
 * Пустое состояние окна выбора кадра (`FramePickerDialog`): лента отдаёт цепочки целиком, а `videoFrames`
 * отсекает клипы и видео уже на клиенте — одна страница может дать 0 плиток, хотя дальше есть что
 * подгрузить. Показываем «картинок нет» только когда подгружать больше нечего — иначе оно мигало бы
 * между страницами, пока «Показать ещё» ещё жива.
 */
export function framePickerEmpty(frameCount: number, hasMore: boolean): boolean {
  return frameCount === 0 && !hasMore;
}

/**
 * Какие картинки показывает окно «Из готового кадра» (задача Оли №340: «по умолчанию пусть
 * показывает те что с отметкой „к публикации“ и загруженные, остальные можно включить нажатием на
 * кнопки»).
 *
 * Флаги — кнопки окна, их можно жать в любом сочетании; отбор идёт ПОВЕРХ `videoFrames`, то есть
 * гейт «это кадр, а не видео» снять нельзя. Все три выключены — показываем всё: пустой экран без
 * единой включённой кнопки сотруднику ничего не объясняет.
 */
export type FramePickerFlags = { marked: boolean; uploaded: boolean; rest: boolean };

export const FRAME_PICKER_DEFAULT: FramePickerFlags = { marked: true, uploaded: true, rest: false };

export function framePickerFilter<T extends FrameRow>(rows: readonly T[], flags: FramePickerFlags): T[] {
  if (!flags.marked && !flags.uploaded && !flags.rest) return [...rows];
  return rows.filter((r) => {
    const marked = r.pick === "picked";
    const uploaded = r.kind === "upload";
    if (flags.marked && marked) return true;
    if (flags.uploaded && uploaded) return true;
    return flags.rest && !marked && !uploaded;
  });
}
